/**
 * The Bundles tab of a `waves` campaign: the outreach waves the owner
 * approves once (the server then sends them). `?wave=<id>` opens a wave's
 * detail in place, `?filter=open|active|all` filters the list.
 */
import { ACTIVE_STATUSES, OPEN_STATUSES, WAVE_FILTERS, lettersToSignText } from "../../lib/b2g";
import { planByMessage, planByWave } from "../../lib/sendPlan";
import { StrategyTable, WaveDetail } from "../../components/b2g/WaveDetail";
import { WavesTable } from "../../components/b2g/WavesTable";
import { useConfirm } from "../../components/confirm/context";
import { QueryBoundary } from "../../components/QueryBoundary";
import { FilterChips } from "../../components/ui/FilterChips";
import type { WavesData } from "./useWavesData";

export function WavesBundles({
  data,
  seat,
  waveId,
  filter,
  setParams,
}: {
  data: WavesData;
  seat: string;
  waveId: string | null;
  filter: string;
  setParams: (patch: Record<string, string | null>) => void;
}) {
  const confirm = useConfirm();
  const planItems = data.plan.data?.items ?? [];
  const horizonDays = data.plan.data?.horizon_days ?? 0;

  if (waveId) {
    return (
      <>
        <button type="button" className="btn btn-sm btn-ghost mb-3" onClick={() => setParams({ wave: null })}>
          ← All waves
        </button>
        <QueryBoundary query={data.wave}>
          {(w) => (
            <WaveDetail
              wave={w}
              plan={planByMessage(planItems)}
              horizonDays={horizonDays}
              busy={data.busy === w.id}
              onAction={async (action, note) => {
                const letters = w.progress?.letters ?? 0;
                const toSign = w.progress?.letters_to_sign ?? 0;
                if (
                  action === "approve" &&
                  !(await confirm({
                    title: `Approve "${w.title}"?`,
                    body:
                      `The server sends ${w.progress?.recipients ?? ""} first emails inside business hours, ` +
                      "and the follow-ups unless someone replies." +
                      (letters ? ` ${letters} ${letters === 1 ? "email carries" : "emails carry"} a PDF letter.` : "") +
                      (toSign ? ` ${lettersToSignText(toSign)} – the server refuses until they are signed.` : ""),
                    confirmLabel: "Approve & send",
                  }))
                ) {
                  return false;
                }
                if (
                  action === "stop" &&
                  !(await confirm({
                    title: "Stop this wave?",
                    body: "Nothing more goes out.",
                    confirmLabel: "Stop wave",
                    tone: "danger",
                  }))
                ) {
                  return false;
                }
                if (
                  action === "reject" &&
                  !(await confirm({
                    title: `Reject "${w.title}"?`,
                    body: "The wave closes for good. The seat reads your note.",
                    confirmLabel: "Reject wave",
                    tone: "danger",
                  }))
                ) {
                  return false;
                }
                try {
                  await data.waveAction.mutateAsync({ w, action, note });
                  return true;
                } catch {
                  return false;
                }
              }}
            />
          )}
        </QueryBoundary>
      </>
    );
  }

  const all = data.waves.data?.waves ?? [];
  const visible =
    filter === "open"
      ? all.filter((w) => OPEN_STATUSES.includes(w.status) || w.status === "checking" || w.status === "changes_requested")
      : filter === "active"
        ? all.filter((w) => ACTIVE_STATUSES.includes(w.status))
        : all;

  return (
    <>
      <div className="flex items-center gap-1.5 mb-4 flex-wrap">
        <FilterChips aria-label="Wave filter" options={WAVE_FILTERS} value={filter} onChange={(value) => setParams({ filter: value })} />
      </div>
      <QueryBoundary
        query={data.waves}
        isEmpty={() => visible.length === 0}
        empty={
          <div className="empty-state">
            <div className="title">{filter === "open" ? "No wave waiting for you" : "No waves here"}</div>
            <p className="hint">{seat} submits a wave with outreach_submit once the research is done.</p>
          </div>
        }
      >
        {() => (
          <WavesTable
            waves={visible}
            plan={data.plan.data ? planByWave(planItems) : undefined}
            horizonDays={horizonDays}
            onOpen={(id) => setParams({ wave: id })}
          />
        )}
      </QueryBoundary>
    </>
  );
}

/** "What works" – reply rate per strategy, language and role across all waves. */
export function WavesStrategy({ data }: { data: WavesData }) {
  const stats = data.waves.data?.strategy_stats ?? [];
  if (!stats.length) return null;
  return (
    <div className="mt-6">
      <h3 className="font-heading font-semibold mb-2">What works – reply rate across all waves</h3>
      <StrategyTable stats={stats} />
    </div>
  );
}
